// §14 — the setting catalog. Defaults live in code; the catalog is also what §12 indexes so
// that typing "accent" in the launcher finds the Personalization page.
import {
  CHOOSABLE_PRESENCE,
  SKIN_IDS,
  SOUND_IDS,
  type ChosenPresence,
  type SkinId,
  type SoundId,
  type ThemePreference,
} from '@hangar/shared';
// §34.16 — the default is a named constant, not a literal typed twice (D-505).
import { WB_MEDIA_SYNC_THRESHOLD_MB_DEFAULT } from '@/config';

/** §11.6 Settings pages, plus `hidden` for persisted state that has no UI of its own. */
export const SETTINGS_PAGES = [
  'personalization',
  'sounds',
  'keyboard',
  'defaultApps',
  'storage',
  'apps',
  'account',
  'about',
  'hidden',
] as const;
export type SettingsPage = (typeof SETTINGS_PAGES)[number];

export type SettingType = 'boolean' | 'number' | 'string' | 'enum' | 'json';

export interface SettingDef<T> {
  label: string;
  page: SettingsPage;
  type: SettingType;
  default: T;
  /** Present for `type: 'enum'`. */
  options?: readonly string[];
  /** Extra words the launcher should match on (§12). */
  keywords?: readonly string[];
}

const setting = <T,>(def: SettingDef<T>): SettingDef<T> => def;

/**
 * The complete v1 setting set. Adding a key here is the only way to add a setting — the store
 * derives its type surface, its defaults and its reset behavior from this object, and
 * `settings.test.ts` asserts the two can never drift apart.
 */
export const SETTINGS = {
  // ── Personalization (§11.6) ─────────────────────────────────────────────────────────────
  skin: setting<SkinId>({
    label: 'Skin',
    page: 'personalization',
    type: 'enum',
    options: SKIN_IDS,
    default: 'house',
    keywords: ['theme', 'appearance', 'windows', 'macos'],
  }),
  theme: setting<ThemePreference>({
    label: 'Appearance',
    page: 'personalization',
    type: 'enum',
    options: ['light', 'dark', 'system'],
    default: 'system',
    keywords: ['light', 'dark', 'colour', 'color'],
  }),
  accent: setting<string>({
    label: 'Accent colour',
    page: 'personalization',
    type: 'string',
    default: '',
    keywords: ['colour', 'highlight'],
  }),
  wallpaper: setting<string>({
    label: 'Wallpaper',
    page: 'personalization',
    type: 'string',
    default: 'hangar-dusk',
    keywords: ['background', 'desktop'],
  }),
  desktopIconSize: setting<number>({
    label: 'Desktop icon size',
    page: 'personalization',
    type: 'number',
    default: 64,
  }),

  // ── Sounds and notifications (§15, §13) ─────────────────────────────────────────────────
  'sound.muted': setting<boolean>({
    label: 'Mute all system sounds',
    page: 'sounds',
    type: 'boolean',
    default: false,
    keywords: ['audio', 'silent'],
  }),
  'sound.disabled': setting<SoundId[]>({
    label: 'Disabled sounds',
    page: 'sounds',
    type: 'json',
    default: [],
    options: SOUND_IDS,
  }),
  /**
   * §26.2 (D-344) — the status this user picked, as against the one the clock derives.
   *
   * A setting rather than a field on the presence row, because the presence row is *live state* —
   * it goes stale, and `derivePresence` overwrites it with `idle` and then `offline` after 90 and
   * 300 seconds. What somebody chose has to outlive that, and a reload, so the heartbeat has
   * something to say when it starts again. `offline` is deliberately not an option: it is what the
   * clock says about a connection that has stopped talking, and choosing it would be contradicted
   * by the very next heartbeat.
   */
  'presence.status': setting<ChosenPresence>({
    label: 'Status',
    page: 'account',
    type: 'enum',
    options: CHOOSABLE_PRESENCE,
    default: 'online',
    keywords: ['presence', 'away', 'busy', 'active', 'online', 'availability'],
  }),
  'notify.doNotDisturb': setting<boolean>({
    label: 'Do not disturb',
    page: 'sounds',
    type: 'boolean',
    default: false,
    keywords: ['notifications', 'dnd', 'quiet'],
  }),

  // ── Keyboard (§6.4, §11.6) ──────────────────────────────────────────────────────────────
  'keymap.overrides': setting<Record<string, string>>({
    label: 'Keyboard overrides',
    page: 'keyboard',
    type: 'json',
    default: {},
    keywords: ['shortcut', 'chord', 'binding'],
  }),

  // ── Arcade (§25.4) ──────────────────────────────────────────────────────────────────────
  //
  // DAS and ARR are the two numbers a Cascade player actually tunes: how long a held direction
  // waits before it starts repeating, and how fast it repeats afterwards. They live in §14 rather
  // than inside the game because the game must stay a pure function of its inputs (§24.4) — the
  // repeat is generated in the input layer and lands in the recorded log, so a replay reproduces
  // the player's exact feel rather than the defaults.
  'arcade.dasMs': setting<number>({
    label: 'Repeat delay (DAS)',
    page: 'keyboard',
    type: 'number',
    default: 133,
    keywords: ['cascade', 'game', 'arcade', 'repeat', 'das'],
  }),
  'arcade.arrMs': setting<number>({
    label: 'Repeat rate (ARR)',
    page: 'keyboard',
    type: 'number',
    default: 33,
    keywords: ['cascade', 'game', 'arcade', 'repeat', 'arr'],
  }),

  // ── Media (§34.6, D-496) ────────────────────────────────────────────────────────────────
  //
  // The one number D-496 turns into a setting: above it a file stays device-local and never syncs,
  // and Explorer badges it "on this device". It is here rather than as a constant so it is visible
  // and searchable (§12) — a file that silently stopped syncing at a threshold nobody could find
  // would look like sync being broken.
  'media.syncThresholdMb': setting<number>({
    label: 'Keep files larger than this on this device (MB)',
    page: 'storage',
    type: 'number',
    default: WB_MEDIA_SYNC_THRESHOLD_MB_DEFAULT,
    keywords: ['sync', 'media', 'video', 'size', 'device', 'local', 'upload'],
  }),

  // ── Workbench, lane B (§34.1, D-505) ────────────────────────────────────────────────────
  //
  // Two settings, both of them a *default* rather than a state: which pane a markdown file opens
  // on, and which chord summons the capture card. Neither is persisted UI state (that is what the
  // `hidden` page is for) — both are things a person changes on purpose and expects to stick.
  'notepad.mdDefaultView': setting<'edit' | 'preview'>({
    label: 'Markdown files open in',
    page: 'defaultApps',
    type: 'enum',
    options: ['edit', 'preview'],
    default: 'edit',
    keywords: ['markdown', 'md', 'preview', 'notepad', 'editor'],
  }),
  /**
   * §34.4 — Quick Notes' capture chord.
   *
   * **Empty means the skin's own default** (house/Windows `Ctrl+Alt+N`, macOS `Meta+Alt+N`), which
   * is what §34.1's "per-skin default from the skin's keymap" requires: a literal default here
   * would be one skin's answer imposed on the other two, and it would go stale the moment a keymap
   * changed. A value overrides the keymap through the same §11.6 mechanism a rebound shell action
   * uses, so there is one arbitration path and not two.
   */
  'notes.captureChord': setting<string>({
    label: 'Quick Notes capture',
    page: 'keyboard',
    type: 'string',
    default: '',
    keywords: ['notes', 'quick', 'capture', 'chord', 'shortcut'],
  }),

  // ── Default apps (§9.2) ─────────────────────────────────────────────────────────────────
  'filetypes.defaults': setting<Record<string, string>>({
    label: 'Default apps',
    page: 'defaultApps',
    type: 'json',
    default: {},
    keywords: ['open with', 'association'],
  }),

  // ── §34.1 / §34.12 — Weather (M65.2) ────────────────────────────────────────────────────
  /**
   * §34.12 — the unit the app renders in. The server is canonical metric; this converts at render.
   *
   * Searchable from §12 like every other setting, which is the reason it has a `page` and a label at
   * all rather than living in the app's own file.
   */
  'weather.units': setting<'imperial' | 'metric'>({
    label: 'Weather units',
    page: 'apps',
    type: 'enum',
    options: ['imperial', 'metric'],
    default: 'imperial',
    keywords: ['weather', 'temperature', 'fahrenheit', 'celsius', 'units', 'metric', 'imperial'],
  }),
  /**
   * §34.12 — the saved locations, "managed in-app".
   *
   * In the settings store rather than in a file under `/Apps/weather/` because §34.12 says so, and the
   * reason holds up: `weather` is the wave's only `requiresAuth` app, so its user always has a server,
   * and the settings store is the thing that already syncs a small list between their devices. A
   * hidden page — the app is the only editor, so a Settings row listing raw coordinates would be a
   * second, worse one.
   */
  'weather.locations': setting<{ name: string; lat: number; lon: number }[]>({
    label: 'Weather locations',
    page: 'hidden',
    type: 'json',
    default: [],
  }),

  // ── Persisted shell state with no page of its own ───────────────────────────────────────
  'ui.stolenChordToastShown': setting<boolean>({
    label: 'Browser-shortcut notice shown',
    page: 'hidden',
    type: 'boolean',
    default: false,
  }),
  'ui.evictionNoticeShown': setting<boolean>({
    label: 'Storage-cleared notice shown',
    page: 'hidden',
    type: 'boolean',
    default: false,
  }),
  /**
   * D-284 — the users whose `~/Desktop` has been written to disk. Until it has, the desktop shows
   * the default set of app icons without any files existing; the first act of organising writes
   * them. A **list**, not a boolean: a guest and a signed-in user have different desktops (§7.1),
   * so one flag would have left whichever came second showing the other's.
   */
  'ui.desktopArranged': setting<string[]>({
    label: 'Desktops the user has arranged',
    page: 'hidden',
    type: 'json',
    default: [],
  }),
  /**
   * D-285 — per user, the app ids their desktop has already been offered an icon for.
   *
   * *Offered*, not *present*: an app whose icon they deleted has been offered, so it stays gone.
   * Tracking what is present instead would make "keep the app list in sync" undo every removal on
   * the next boot, which is the same operation answering the opposite question.
   */
  'ui.desktopOfferedApps': setting<Record<string, string[]>>({
    label: 'Apps each desktop has been offered',
    page: 'hidden',
    type: 'json',
    default: {},
  }),
} satisfies Record<string, SettingDef<unknown>>;

export type SettingId = keyof typeof SETTINGS;

export type SettingValue<K extends SettingId> =
  (typeof SETTINGS)[K] extends SettingDef<infer T> ? T : never;

export const SETTING_IDS = Object.keys(SETTINGS) as SettingId[];

export function settingDef<K extends SettingId>(id: K): (typeof SETTINGS)[K] {
  return SETTINGS[id];
}

/** Defaults as a plain object — the store's starting state and what `reset()` restores to. */
export function defaultSettings(): { [K in SettingId]: SettingValue<K> } {
  const out: Record<string, unknown> = {};
  for (const id of SETTING_IDS) {
    const value = (SETTINGS[id] as SettingDef<unknown>).default;
    // Structured-clone so a mutable default (an array, a record) is never shared between
    // the catalog and a live store.
    out[id] = structuredClone(value);
  }
  return out as { [K in SettingId]: SettingValue<K> };
}
